import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../api/services/contract/models";
import { IUserInfoResponseData } from "../api/services/user/models";

type UserIdentifierType =
  | Partial<UserProfile>
  | IUserInfoResponseData
  | undefined
  | null;

/**
 * Extracts the user identifier from a user object.
 * Checks in order: id -> userIdentification -> userId
 * @param user - User object that can be Partial<UserProfile>, IUserInfoResponseData, or undefined
 * @returns The user identifier (id, userIdentification, or userId) as a string, or undefined
 */
export function getUserIdentifier(
  user: UserIdentifierType
): string | number | undefined {
  if (!user) {
    return undefined;
  }

  // Check userIdentification (available on both types)
  if (
    user.userIdentification !== undefined &&
    user.userIdentification !== null
  ) {
    return user.userIdentification;
  }

  // Check userId (only available on UserProfile)
  if ("userId" in user && user.userId !== undefined && user.userId !== null) {
    return (user as Partial<UserProfile>).userId;
  }

  // Check id first (available on both types)
  if (user.id !== undefined && user.id !== null) {
    return user.id;
  }

  return undefined;
}

/**
 * Compares two user identifiers, handling all possible identifier types.
 * Converts both to strings for comparison to handle type mismatches.
 * @param identifier1 - First identifier (can be string, number, or undefined)
 * @param identifier2 - Second identifier (can be string, number, or undefined)
 * @returns true if identifiers match, false otherwise
 */
export function compareUserIdentifiers(
  identifier1: string | number | undefined | null,
  identifier2: string | number | undefined | null
): boolean {
  if (identifier1 === undefined || identifier1 === null) {
    return false;
  }
  if (identifier2 === undefined || identifier2 === null) {
    return false;
  }
  return String(identifier1) === String(identifier2);
}

/**
 * Checks if a user matches a given identifier.
 * Checks against all possible identifier properties: id, userIdentification, and userId.
 * @param user - User object that can be Partial<UserProfile>, IUserInfoResponseData, or undefined
 * @param identifier - Identifier to compare against (can be string, number, or undefined)
 * @returns true if ANY of the user's identifiers (id, userIdentification, or userId) matches the provided identifier, false otherwise
 */
export function userMatchesIdentifier(
  user: UserIdentifierType,
  identifier: string | number | undefined | null
): boolean {
  if (!user || identifier === undefined || identifier === null) {
    return false;
  }

  // Check id (available on both types)
  if (
    user.id !== undefined &&
    user.id !== null &&
    compareUserIdentifiers(user.id, identifier)
  ) {
    return true;
  }

  // Check userIdentification (available on both types)
  if (
    user.userIdentification !== undefined &&
    user.userIdentification !== null &&
    compareUserIdentifiers(user.userIdentification, identifier)
  ) {
    return true;
  }

  // Check userId (only available on UserProfile)
  if (
    "userId" in user &&
    (user as Partial<UserProfile>).userId !== undefined &&
    (user as Partial<UserProfile>).userId !== null &&
    compareUserIdentifiers((user as Partial<UserProfile>).userId, identifier)
  ) {
    return true;
  }

  return false;
}

export const getUserIdentificationFromContractParties = (
  contract?: IContractDetailsResponseData,
  userRole?: ESenderRole
) => {
  if (!contract || !userRole) {
    return undefined;
  }

  const partyUserIdentification = contract.parties?.find(
    (p) => p.role === userRole
  )?.userId;

  if (partyUserIdentification) {
    return partyUserIdentification;
  }

  const partyProfileUserIdentification =
    contract.partiesProfiles?.[userRole]?.[0]?.userIdentification; //TODO: handle multiple parties

  if (partyProfileUserIdentification) {
    return String(partyProfileUserIdentification);
  }

  if (
    userRole === ESenderRole.PROVIDER &&
    contract.provider?.userIdentification
  ) {
    return String(contract.provider?.userIdentification);
  } else if (
    userRole === ESenderRole.CUSTOMER &&
    contract.customer?.userIdentification
  ) {
    return String(contract.customer?.userIdentification);
  }
  return undefined;
};
